import { create } from 'zustand';
import type {
  StyleLevel,
  StyleLevelTier,
  StyleBadge,
  BadgeId,
  StreakData,
  UserGamificationProfile,
} from '../types';

// ─── Style level definitions ────────────────────────────────────────────────
export const STYLE_LEVELS: StyleLevel[] = [
  { tier: 'style-rookie', label: 'Style Rookie', minPoints: 0, icon: '👟', gradientColors: ['#7EC8A0', '#5B8DEF'] },
  { tier: 'fashion-enthusiast', label: 'Fashion Enthusiast', minPoints: 100, icon: '👗', gradientColors: ['#7C3AED', '#EC4899'] },
  { tier: 'fashion-icon', label: 'Fashion Icon', minPoints: 500, icon: '👑', gradientColors: ['#F59E0B', '#EF4444'] },
  { tier: 'trendsetter', label: 'Trendsetter', minPoints: 2000, icon: '🔥', gradientColors: ['#EC4899', '#F59E0B'] },
];

// ─── Badge milestone definitions ────────────────────────────────────────────
export const BADGE_DEFINITIONS: StyleBadge[] = [
  { id: 'first-upload', label: 'First Upload', description: 'Uploaded your first outfit', icon: '📸' },
  { id: 'ten-ratings', label: 'Critic', description: 'Rated 10 outfits', icon: '⭐' },
  { id: 'fifty-ratings', label: 'Style Judge', description: 'Rated 50 outfits', icon: '🏅' },
  { id: 'seven-day-streak', label: 'On Fire', description: '7-day activity streak', icon: '🔥' },
  { id: 'thirty-day-streak', label: 'Unstoppable', description: '30-day activity streak', icon: '💎' },
];

export function getLevelForPoints(points: number): StyleLevel {
  let level = STYLE_LEVELS[0];
  for (const l of STYLE_LEVELS) {
    if (points >= l.minPoints) level = l;
  }
  return level;
}

interface CelebrationItem {
  type: 'badge' | 'level';
  data: StyleBadge | StyleLevel;
}

interface GamificationState {
  profile: UserGamificationProfile | null;
  isLoading: boolean;
  celebrations: CelebrationItem[];
  setProfile: (profile: UserGamificationProfile) => void;
  setLoading: (loading: boolean) => void;
  addCelebration: (item: CelebrationItem) => void;
  dismissCelebration: () => void;
  checkMilestones: (uploads: number, ratings: number, streak: number) => void;
}

export const useGamificationStore = create<GamificationState>((set, get) => ({
  profile: null,
  isLoading: false,
  celebrations: [],
  setProfile: (profile) => set({ profile }),
  setLoading: (isLoading) => set({ isLoading }),
  addCelebration: (item) => set((s) => ({ celebrations: [...s.celebrations, item] })),
  dismissCelebration: () => set((s) => ({ celebrations: s.celebrations.slice(1) })),
  checkMilestones: (uploads, ratings, streak) => {
    const { profile } = get();
    if (!profile) return;
    const earned = profile.badges.filter((b) => b.earnedAt).map((b) => b.id);
    const newBadges: BadgeId[] = [];
    if (uploads >= 1 && !earned.includes('first-upload')) newBadges.push('first-upload');
    if (ratings >= 10 && !earned.includes('ten-ratings')) newBadges.push('ten-ratings');
    if (ratings >= 50 && !earned.includes('fifty-ratings')) newBadges.push('fifty-ratings');
    if (streak >= 7 && !earned.includes('seven-day-streak')) newBadges.push('seven-day-streak');
    if (streak >= 30 && !earned.includes('thirty-day-streak')) newBadges.push('thirty-day-streak');
    if (newBadges.length > 0) {
      const now = new Date().toISOString();
      const updatedBadges = profile.badges.map((b) =>
        newBadges.includes(b.id) ? { ...b, earnedAt: now } : b
      );
      const newPoints = profile.totalPoints + newBadges.length * 50;
      const newLevel = getLevelForPoints(newPoints);
      const levelChanged = newLevel.tier !== profile.styleLevel.tier;
      set({
        profile: { ...profile, badges: updatedBadges, totalPoints: newPoints, styleLevel: newLevel },
        celebrations: [
          ...get().celebrations,
          ...newBadges.map((id) => ({
            type: 'badge' as const,
            data: BADGE_DEFINITIONS.find((b) => b.id === id)!,
          })),
          ...(levelChanged ? [{ type: 'level' as const, data: newLevel }] : []),
        ],
      });
    }
  },
}));
